<template>
  <b-modal :title="helper.trans('labels.withdrawal_whitelist')"
           dialog-class="modal-lg"
           :modelValue="modal !== null"
           footer-class=""
           @update:modelValue="$event === false && (modal = null)">
    <template #default>
      <LoaderWrapper ref="loader">
      <ConfirmModal ref="confirmDelete"></ConfirmModal>
    <div class="row" v-if="account">
      <div v-if="!account.withdrawalWhitelistsEnabled" class="col-sm-12 text-center">
        <span class="me-2 text-danger">
          {{ helper.trans('labels.whitelists_disabled')}}
        </span>
        <button v-if="!isMerchantOperator" @click.prevent="enable()" type="button" class="btn btn-sm btn-outline-dark">{{ helper.trans('labels.enable')}}</button>
      </div>
      <div v-else class="col-sm-12 text-center">
        <span class="me-2 text-success">
          {{ helper.trans('labels.whitelists_enabled')}}
        </span>
        <button v-if="!isMerchantOperator" @click.prevent="disable()" type="button" class="btn btn-sm btn-outline-danger">{{ helper.trans('labels.disable')}}</button>
      </div>

      <div class="col-sm-12 mt-2">

        <table class="table table-striped border-light align-middle text-left text-nowrap position-relative">
          <thead>
          <tr>
            <th class="text-capitalize-first">{{ helper.trans('labels.address.usdt') }}</th>
            <th class="text-capitalize-first">{{ helper.trans('labels.created_at') }}</th>
            <th class="text-capitalize-first">{{ helper.trans('labels.deleted_at') }}</th>
            <th>
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in withdrawalWhitelists">
            <td>{{item.address}}</td>
            <td>{{dateHelper.format(item.createdAt)}}</td>
            <td>{{dateHelper.format(item.deletedAt)}}</td>
            <td>
              <button v-if="!item.deletedAt" type="button" @click="remove(item)" class="btn btn-sm btn-outline-danger text-nowrap float-end"><i class="fa fa-trash"></i></button>
            </td>
          </tr>
          <tr v-for="item in newItems">
            <td colspan="3">
              <FormInputWrapper v-slot="parentProps" :errors="getErrors(`${item.id}_address`)">
              <input type="text" :class="parentProps.class" v-model="item.address" class="form-control form-control-sm">
              </FormInputWrapper>
            </td>
            <td>
              <button type="button" @click="cancel(item)" class="btn btn-sm btn-outline-dark text-nowrap float-end"><i class="fa fa-cancel"></i></button>
              <button type="button" @click="save(item)" class="btn btn-sm btn-outline-success text-nowrap me-2 float-end"><i class="fa fa-save"></i></button>
            </td>
          </tr>
          <tr>
            <td colspan="4" v-if="!isMerchantOperator">
              <button @click.prevent="add()" type="button" class="btn btn-sm btn-outline-dark text-nowrap text-capitalize-first w-100"><i class="fa fa-plus"></i> {{helper.trans('button.add')}}</button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
      </LoaderWrapper>
    </template>
    <template #footer>
      <button type="button" @click="modal = null" class="btn btn-outline-danger btn-sm text-capitalize-first float-end">
        {{ helper.trans('button.ok') }}
      </button>
    </template>
  </b-modal>
</template>

<script lang="ts">

import {computed, defineComponent, ref} from 'vue'
import {reactive, toRefs} from "@vue/reactivity";
import {BModal} from "bootstrap-vue-3";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {helper} from "@/service/CommonHelper";
import APIClient from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import CopyButton from "@/components/CopyButton.vue";
import {numberFormatter} from "@/service/NumberFormatter";
import AccountDTO from "@/models/AccountDTO";
import WithdrawalWhitelistDTO from "@/models/WithdrawalWhitelistDTO";
import PaginationDTO from "@/models/PaginationDTO";
import {dateHelper} from "@/service/DateHelper";
import ConfirmModal, {ConfirmModalProps} from "@/components/ConfirmModal.vue";
import TronWeb from "tronweb";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import {useStore} from "vuex";

const api = new APIClient();
const notification = useNotification()
let id = 0;

export default defineComponent({
  emits: ['updated'],
  components: {
    ConfirmModal,
    BModal,
    LoaderWrapper,
    CopyButton,
    FormInputWrapper
  },

  setup({}, {emit}) {

    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const confirmDelete = ref<InstanceType<typeof ConfirmModal> | null>(null)
    const store = useStore()
    const state = reactive({
      modal: null as boolean|null,
      account: null as AccountDTO|null,
      newItems: [] as WithdrawalWhitelistDTO[],
      withdrawalWhitelists: [] as WithdrawalWhitelistDTO[],
      errors: {} as ValidationErrorDTO[]|any,
    });

    const getForm = () => {
      return {}
    }

    const getErrors = (path: string) => {
      if (!state.errors.hasOwnProperty(path)) {
        return [];
      }

      return state.errors[path]
    }

    const setErrors = (id: number, data: ValidationErrorDTO[]) => {
      state.errors = {};
      for (const error of data) {
        if (!state.errors.hasOwnProperty(id + '_' + error.field)) {
          state.errors[id + '_' + error.field] = [];
        }
        state.errors[id + '_' + error.field].push(error)
      }
    }

    const show = (account: AccountDTO) => {
      state.withdrawalWhitelists = []
      state.newItems = []
      state.modal = true
      state.account = account
      state.errors = {}

      loadList()
    }

    const loadList = async () => {
      loader.value?.show()
      try {
        state.withdrawalWhitelists = (await api.withdrawalWhitelist({
          limit: 1000,
          offset: 0
        } as PaginationDTO, {accountIds: [state.account!.id]})).items
      } finally {
        loader.value?.hide()
      }
    }

    const add = async () => {
      id++;
      state.newItems.push({
        id: id,
        accountId: state.account!.id,
        address: "",
        createdAt: "",
        deletedAt: "",
        activeFrom: "",
      } as WithdrawalWhitelistDTO)
    }

    const remove = async (item: WithdrawalWhitelistDTO) => {
      confirmDelete.value?.show({
        title: helper.trans('labels.delete_confirm'),
        okTitle: helper.trans(`button.delete`),
        cancelTitle: null,
        onOkClick: async () => {
          loader.value?.show()
          try {
            await api.withdrawalWhitelistDelete(item.id)
          } finally {
            loader.value?.hide()
          }
          loadList()
        }
      } as ConfirmModalProps)
    }

    const save = async (item: WithdrawalWhitelistDTO) => {
      setErrors(item.id, [])
      let addressIsValid = true;
      try {
        let hex = TronWeb.address.toHex(item.address)
        if (!hex) {
          addressIsValid = false
        }
      } catch (e) {
        addressIsValid = false;
      }

      if (!addressIsValid) {
        setErrors(item.id, [{
          code: "INVALID_VALUE",
          field: "address",
          message: "Invalid Tron address"
        } as ValidationErrorDTO])
        return;
      }

      loader.value?.show()
      try {
        await api.withdrawalWhitelistCreate(item)
        cancel(item)
      } catch (e: any) {
        if (e.response && e.request && e.request.status === 400 && e.response.data) {
          setErrors(item.id, e.response.data)
        } else {
          console.log(e);
        }
        return
      } finally {
        loader.value?.hide()
      }
      loadList()

    }

    const cancel = async (item: WithdrawalWhitelistDTO) => {
      state.newItems = state.newItems.filter((old) => old.id !== item.id)
    }

    const enable = async () => {
      loader.value?.show()
      try {
        state.account = await api.accountWithdrawalWhitelistToggle(state.account!.id, 'enable')
        emit('updated')
      } finally {
        loader.value?.hide()
      }

    }

    const disable = async () => {
      loader.value?.show()
      try {
        state.account = await api.accountWithdrawalWhitelistToggle(state.account!.id, 'disable')
        emit('updated')
      } finally {
        loader.value?.hide()
      }

    }

    return {
      ...toRefs(state),
      getErrors,
      show,
      helper,
      getForm,
      numberFormatter,
      dateHelper,
      cancel,
      save,
      add,
      remove,
      loader,
      confirmDelete,
      disable,
      enable,
      isMerchantOperator: computed<boolean>(() => store.getters.isMerchantOperator),
    }
  }
})
</script>
